/**
 * ============================================================================
 * TWO-FACTOR AUTHENTICATION SERVICE - Frontend
 * ============================================================================
 * 
 * Frontend service for 2FA operations:
 * - Setup and enable 2FA
 * - Verify 2FA codes
 * - Manage backup codes
 * - Disable 2FA
 */

import api from './api';

/**
 * Handle API errors
 */
const handleApiError = (error) => {
  if (error.response) {
    return error.response.data;
  }
  return { message: error.message || 'An error occurred' };
};

/**
 * Generate 2FA secret and QR code
 * @returns {Promise<Object>} Secret, QR code, and backup codes
 */
export const generate2FASecret = async () => {
  try {
    const response = await api.post('/auth/2fa/setup');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Verify 2FA code during setup
 * @param {string} code - 6-digit TOTP code
 * @returns {Promise<Object>} Verification result
 */
export const verify2FASetup = async (code) => {
  try {
    const response = await api.post('/auth/2fa/verify-setup', { code });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Enable 2FA
 * @param {string} code - 6-digit TOTP code
 * @returns {Promise<Object>} Enable result
 */
export const enable2FA = async (code) => {
  try {
    const response = await api.post('/auth/2fa/enable', { code });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Disable 2FA
 * @param {string} code - 6-digit TOTP code or backup code
 * @param {string} password - User password for confirmation
 * @returns {Promise<Object>} Disable result
 */
export const disable2FA = async (code, password) => {
  try {
    const response = await api.post('/auth/2fa/disable', { code, password });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Verify 2FA code during login
 * @param {string} code - 6-digit TOTP code or backup code
 * @returns {Promise<Object>} Verification result with tokens
 */
export const verify2FALogin = async (code) => {
  try {
    const response = await api.post('/auth/2fa/verify-login', { code });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get backup codes
 * @returns {Promise<Array<string>>} Array of backup codes
 */
export const getBackupCodes = async () => {
  try {
    const response = await api.get('/auth/2fa/backup-codes');
    return response.data.codes;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Regenerate backup codes
 * @param {string} code - 6-digit TOTP code for verification
 * @returns {Promise<Array<string>>} New backup codes
 */
export const regenerateBackupCodes = async (code) => {
  try {
    const response = await api.post('/auth/2fa/regenerate-backup-codes', { code });
    return response.data.codes;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Check if 2FA is enabled for current user
 * @returns {Promise<boolean>} True if 2FA is enabled
 */
export const is2FAEnabled = async () => {
  try {
    const response = await api.get('/auth/2fa/status');
    return response.data.enabled;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get 2FA status and details
 * @returns {Promise<Object>} 2FA status information
 */
export const get2FAStatus = async () => {
  try {
    const response = await api.get('/auth/2fa/status');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Verify backup code
 * @param {string} backupCode - Backup code to verify
 * @returns {Promise<boolean>} True if backup code is valid
 */
export const verifyBackupCode = async (backupCode) => {
  try {
    const response = await api.post('/auth/2fa/verify-backup-code', { backupCode });
    return response.data.valid;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Request 2FA recovery
 * @param {string} email - User email
 * @param {string} phone - User phone number
 * @returns {Promise<Object>} Recovery result
 */
export const request2FARecovery = async (email, phone) => {
  try {
    const response = await api.post('/auth/2fa/recovery', { email, phone });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Complete 2FA recovery
 * @param {string} recoveryToken - Recovery token
 * @param {string} newCode - New 2FA code to set
 * @returns {Promise<Object>} Recovery completion result
 */
export const complete2FARecovery = async (recoveryToken, newCode) => {
  try {
    const response = await api.post('/auth/2fa/recovery/complete', {
      recoveryToken,
      newCode,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  generate2FASecret,
  verify2FASetup,
  enable2FA,
  disable2FA,
  verify2FALogin,
  getBackupCodes,
  regenerateBackupCodes,
  is2FAEnabled,
  get2FAStatus,
  verifyBackupCode,
  request2FARecovery,
  complete2FARecovery,
};
